import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Airplane, CheckCircle, WarningCircle, FileArrowDown, Package, Eye } from "@phosphor-icons/react";
import { DOC_TYPE_LABELS, DOC_TYPE_COLORS, REQUIRED_DOC_TYPES, API } from "@/lib/api";
import { Toaster } from "sonner";

const publicApi = axios.create({ baseURL: API });

export default function PublicShipment() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const [previewCt, setPreviewCt] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data: s } = await publicApi.get(`/public/shipments/${token}`);
        setData(s);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Invalid or revoked link");
      }
    })();
  }, [token]);

  const openPreview = (d) => {
    const url = `${API}/public/shipments/${token}/documents/${d.id}/download`;
    setPreviewUrl(url);
    setPreviewName(d.original_filename);
    setPreviewCt(d.content_type || "");
  };

  if (err) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <WarningCircle size={40} weight="duotone" className="text-[#FF4F00] mx-auto mb-3" />
          <div className="font-display font-black text-2xl text-zinc-950">Link unavailable</div>
          <div className="text-sm text-zinc-500 mt-2">{err}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500 font-mono">Loading shared shipment…</div>;
  }

  const uploaded = data.documents.reduce((m, d) => {
    (m[d.doc_type] = m[d.doc_type] || []).push(d);
    return m;
  }, {});

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header bar */}
      <div className="bg-zinc-950 text-white px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Airplane size={22} weight="duotone" className="text-[#FF4F00]" />
            <div>
              <div className="font-display font-black text-base leading-none">TOM <span className="text-[#FF4F00]">CARGO</span> DOCS</div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-0.5">Shared with you</div>
            </div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Read-only</div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6">
        {/* Shipment header */}
        <div className="bg-white border border-zinc-200 p-6 mb-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2">Shipment</div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-zinc-950 font-mono" data-testid="pub-awb">
              {data.awb_number}
            </h1>
            {data.is_complete ? (
              <span className="inline-flex items-center gap-1 text-xs font-mono border border-emerald-200 bg-emerald-50 text-emerald-800 px-2.5 py-1">
                <CheckCircle size={14} weight="fill" /> ALL DOCS
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-mono border border-orange-200 bg-orange-50 text-[#FF4F00] px-2.5 py-1">
                <WarningCircle size={14} weight="fill" /> {data.docs_missing.length} PENDING
              </span>
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
            <Meta label="Date" value={data.doc_date || "—"} mono />
            <Meta label="Consignee" value={data.consignee || "—"} />
            <Meta label="Customs 1" value={data.customs_no_1 || "—"} mono />
            <Meta label="Customs 2" value={data.customs_no_2 || "—"} mono />
          </div>
          {data.notes && <div className="mt-3 text-sm text-zinc-600 italic border-l-2 border-zinc-200 pl-3">{data.notes}</div>}
        </div>

        {/* Docs */}
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-3">Documents in this pack</div>
        <div className="space-y-3">
          {[...REQUIRED_DOC_TYPES, "other"].map((t) => {
            const docs = uploaded[t] || [];
            const isRequired = REQUIRED_DOC_TYPES.includes(t);
            return (
              <div key={t} className="bg-white border border-zinc-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-block px-2 py-0.5 text-[11px] font-mono border ${DOC_TYPE_COLORS[t]}`}>
                    {DOC_TYPE_LABELS[t]}
                  </span>
                  {isRequired && docs.length === 0 && (
                    <span className="text-[10px] font-mono text-[#FF4F00] uppercase tracking-widest">Pending</span>
                  )}
                  {docs.length > 0 && <CheckCircle size={14} weight="fill" className="text-emerald-600" />}
                </div>
                {docs.length === 0 ? (
                  <div className="text-xs text-zinc-400 italic">Not yet uploaded</div>
                ) : (
                  <div className="space-y-2">
                    {docs.map((d) => (
                      <div key={d.id} className="flex items-center justify-between text-sm border border-zinc-100 bg-zinc-50 px-3 py-2">
                        <div className="truncate flex-1 pr-2">
                          <div className="text-zinc-900 truncate">{d.original_filename}</div>
                          <div className="text-[10px] font-mono text-zinc-400">{(d.size / 1024).toFixed(1)} KB</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => openPreview(d)}
                            className="text-zinc-700 p-1 hover:bg-white transition-colors duration-150" title="Preview">
                            <Eye size={14} />
                          </button>
                          <a href={`${API}/public/shipments/${token}/documents/${d.id}/download`}
                            className="text-[#002FA7] p-1 hover:bg-blue-50 transition-colors duration-150" title="Download">
                            <FileArrowDown size={14} />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-center mt-8 text-[10px] uppercase tracking-widest text-zinc-400 font-mono">
          Powered by Tom Cargo Docs sys · Read-only share
        </div>
      </div>

      {previewUrl && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreviewUrl(null)}>
          <div className="bg-white w-full max-w-5xl h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
              <div className="text-sm font-medium text-zinc-900 truncate">{previewName}</div>
              <button onClick={() => setPreviewUrl(null)} className="text-zinc-500 hover:text-zinc-900">✕</button>
            </div>
            <div className="flex-1 bg-zinc-100 overflow-auto flex items-center justify-center">
              {previewCt.startsWith("image/") ? (
                <img src={previewUrl} alt={previewName} className="max-w-full max-h-full object-contain" />
              ) : previewCt === "application/pdf" ? (
                <iframe title={previewName} src={previewUrl} className="w-full h-full bg-white" />
              ) : (
                <div className="text-center p-8">
                  <div className="text-sm text-zinc-600 mb-3">No inline preview available.</div>
                  <a href={previewUrl} className="bg-[#002FA7] text-white text-sm px-4 py-2 hover:bg-[#00227A]">Download</a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Toaster position="bottom-right" richColors closeButton />
    </div>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{label}</div>
      <div className={`text-sm text-zinc-900 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
